import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import { CLIENT_STATUS_MAP } from '../../utils/constants';
import { useAuth } from '../../contexts/AuthContext';

function getClientPhotoUrl(photoPath, version) {
  if (!photoPath) return '';
  var path = String(photoPath).replace(/\\/g, '/');
  if (/^https?:\/\//i.test(path)) return path + (path.indexOf('?') >= 0 ? '&' : '?') + 'v=' + encodeURIComponent(version || '1');
  var apiBase = (api.defaults && api.defaults.baseURL) || process.env.REACT_APP_API_URL || '';
  var origin = /^https?:\/\//i.test(apiBase) ? apiBase.replace(/\/api\/v1\/?$/, '').replace(/\/$/, '') : (typeof window !== 'undefined' ? window.location.origin : '');
  var url = origin + (path.charAt(0) === '/' ? path : '/' + path);
  return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'v=' + encodeURIComponent(version || '1');
}

export default function ClientList() {
  const location = useLocation();
  const navigate = useNavigate();
  const convertLeadId = new URLSearchParams(location.search).get('convertLead');
  
  const { user } = useAuth();
  const canViewApprovals = user?.role?.name === 'Admin' || user?.role?.name === 'Manager';
  
  const [clients, setClients] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  
  // Filter states
  const [showFilters] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [clientTypeFilter, setClientTypeFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [branches, setBranches] = useState([]);
  
  // Export dropdown
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  
  const abortControllerRef = useRef(null);

  // Fetch branches for filter dropdown
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await api.get('/branches?limit=100');
        setBranches(res.data?.data?.branches || []);
      } catch (err) {
        console.error("Failed to fetch branches:", err);
      }
    };
    fetchBranches();
  }, []);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (convertLeadId) {
      navigate(`/clients/onboard/${convertLeadId}`, { replace: true });
    }
  }, [convertLeadId, navigate]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, clientTypeFilter, branchFilter, dateFrom, dateTo]);

  useEffect(() => { 
    fetchClients(); 
  }, [page, debouncedSearch, statusFilter, clientTypeFilter, branchFilter, dateFrom, dateTo]);

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append('page', page);
    if (debouncedSearch) params.append('search', debouncedSearch);
    if (statusFilter) params.append('status', statusFilter);
    if (clientTypeFilter) params.append('clientType', clientTypeFilter);
    if (branchFilter) params.append('branch', branchFilter);
    if (dateFrom) params.append('startDate', dateFrom);
    if (dateTo) params.append('endDate', dateTo);
    return params.toString();
  };

  const fetchClients = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    setLoading(true);
    try {
      const res = await api.get(`/clients?${buildQueryParams()}`, {
        signal: controller.signal
      });
      setClients(res.data?.data?.clients || []);
      setPagination(res.data?.data?.pagination || null);
    } catch (err) {
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
        console.error("Failed to fetch clients:", err);
        setClients([]);
        setPagination(null);
      }
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false);
      }
    }
  };

  const handleExport = async (format = 'csv') => {
    setShowExportMenu(false);
    setIsExporting(true);
    
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (statusFilter) params.append('status', statusFilter);
      if (clientTypeFilter) params.append('clientType', clientTypeFilter);
      if (branchFilter) params.append('branch', branchFilter);
      if (dateFrom) params.append('startDate', dateFrom);
      if (dateTo) params.append('endDate', dateTo);
      // Export every matching record, not only the current page or the
      // first server page. Fetch in bounded batches to avoid large requests.
      params.append('limit', '1000');
      let exportPage = 1;
      let totalPages = 1;
      const allClients = [];
      do {
        params.set('page', String(exportPage));
        const res = await api.get(`/clients?${params.toString()}`);
        const payload = res.data?.data || {};
        allClients.push(...(payload.clients || []));
        totalPages = Math.max(1, Number(payload.pagination?.pages) || 1);
        exportPage += 1;
      } while (exportPage <= totalPages);

      if (allClients.length === 0) {
        alert("No data to export");
        setIsExporting(false);
        return;
      }

      if (format === 'csv') {
        exportCSV(allClients);
      } else if (format === 'excel') {
        exportExcel(allClients);
      }
    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to export data.");
    } finally {
      setIsExporting(false);
    }
  };

  const escapeCsv = (val) => {
    const str = String(val ?? '');
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const exportCSV = (data) => {
    const headers = ["Client ID", "Name", "Phone", "Email", "Status", "Tags", "Active Loans", "Branch", "Created Date"];
    const csvRows = [];
    csvRows.push(headers.map(escapeCsv).join(','));

    data.forEach(client => {
      const row = [
        escapeCsv(client.clientNumber || ''),
        escapeCsv(`${client.firstName || ''} ${client.lastName || ''}`),
        escapeCsv(client.phone || ''),
        escapeCsv(client.email || ''),
        escapeCsv(client.status || ''),
        escapeCsv([client.clientActivityClass ? client.clientActivityClass.toUpperCase() : '', (client.activeLoans || client.computedActiveLoans) > 0 ? 'ACTIVE LOAN' : ''].filter(Boolean).join(' | ')),
        escapeCsv(client.activeLoans || client.computedActiveLoans || 0),
        escapeCsv(client.branch ? (typeof client.branch === 'object' ? client.branch.name : client.branch) : ''),
        escapeCsv(client.createdAt ? new Date(client.createdAt).toLocaleDateString() : '')
      ];
      csvRows.push(row.join(','));
    });

    const csvString = csvRows.join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvString], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `clients_export_${new Date().toISOString().slice(0,10)}.csv`);
  };

  const exportExcel = (data) => {
    const headers = ["Client ID", "Name", "Phone", "Email", "Status", "Tags", "Active Loans", "Branch", "Created Date"];
    
    let xml = '<?xml version="1.0"?>\n';
    xml += '<?mso-application progid="Excel.Sheet"?>\n';
    xml += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"\n';
    xml += ' xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n';
    xml += '<Worksheet ss:Name="Clients"><Table>\n';
    
    xml += '<Row>';
    headers.forEach(h => {
      xml += `<Cell><Data ss:Type="String">${escapeXml(h)}</Data></Cell>`;
    });
    xml += '</Row>\n';
    
    data.forEach(client => {
      xml += '<Row>';
      xml += `<Cell><Data ss:Type="String">${escapeXml(client.clientNumber || '')}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="String">${escapeXml(`${client.firstName || ''} ${client.lastName || ''}`)}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="String">${escapeXml(client.phone || '')}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="String">${escapeXml(client.email || '')}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="String">${escapeXml(client.status || '')}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="String">${escapeXml([client.clientActivityClass ? client.clientActivityClass.toUpperCase() : '', (client.activeLoans || client.computedActiveLoans) > 0 ? 'ACTIVE LOAN' : ''].filter(Boolean).join(' | '))}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="Number">${Number(client.activeLoans || client.computedActiveLoans || 0)}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="String">${escapeXml(client.branch ? (typeof client.branch === 'object' ? client.branch.name : client.branch) : '')}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="String">${escapeXml(client.createdAt ? new Date(client.createdAt).toLocaleDateString() : '')}</Data></Cell>`;
      xml += '</Row>\n';
    });
    
    xml += '</Table></Worksheet></Workbook>';
    
    const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
    downloadBlob(blob, `clients_export_${new Date().toISOString().slice(0,10)}.xls`);
  };

  const escapeXml = (val) => {
    return String(val ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setClientTypeFilter('');
    setBranchFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = statusFilter || clientTypeFilter || branchFilter || dateFrom || dateTo;
  const activeFilterCount = [statusFilter, clientTypeFilter, branchFilter, dateFrom, dateTo].filter(Boolean).length;

  const handleDelete = async (id) => {
    if (deletingId) return;
    
    const isConfirmed = window.confirm(
      '⚠️ WARNING: This will permanently delete this client and all associated data. This action cannot be undone.\n\nAre you sure?'
    );
    if (!isConfirmed) return;

    setDeletingId(id);
    
    try {
      await api.delete(`/clients/${id}`);
      setClients(prev => prev.filter(c => c._id !== id));
      setPagination(prev => prev ? { ...prev, total: prev.total - 1 } : null);
      alert('Client permanently deleted successfully.');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete client.';
      alert(msg);
      fetchClients();
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      header: 'National ID',
      render: (row) => (
        <Link to={`/clients/${row._id}`} style={{ color: 'var(--primary)', fontWeight: 600, fontFamily: 'monospace', fontSize: '13px' }}>
          {row.clientNumber || 'N/A'}
        </Link>
      )
    },
    { 
      header: 'Client',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 210 }}>
          <div style={{ position: 'relative', width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: '#eef2ff', color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, border: '2px solid #e0e7ff' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>{(row.firstName || 'C')[0]}{(row.lastName || '')[0]}</span>
            {(row.profilePicture || row.passportPhotoUrl) && <img src={getClientPhotoUrl(row.profilePicture || row.passportPhotoUrl, row.updatedAt || row._id)} alt={`${row.firstName || ''} profile`} onError={(e) => { if (e.currentTarget) { e.currentTarget.onerror = null; e.currentTarget.style.display = 'none'; } }} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
          </div>
          <div style={{ minWidth: 0 }}>
          <span style={{ fontWeight: 700, color: '#111827' }}>{row.firstName} {row.lastName}</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
            {row.isNewClient && <span style={{ padding: '2px 7px', borderRadius: 999, fontSize: 10, fontWeight: 800, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>NEW CLIENT</span>}
            {row.isInactive && <span style={{ padding: '2px 7px', borderRadius: 999, fontSize: 10, fontWeight: 800, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}>INACTIVE (NO LOAN)</span>}
            {(row.activeLoans || row.computedActiveLoans) > 0 && <span style={{ padding: '2px 7px', borderRadius: 999, fontSize: 10, fontWeight: 800, background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' }}>ACTIVE LOAN</span>}
            {(row.riskRating === 'high' || row.riskRating === 'very-high') && <span style={{ padding: '2px 7px', borderRadius: 999, fontSize: 10, fontWeight: 800, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>HIGH RISK</span>}
            {row.isDormant && <span style={{ padding: '2px 7px', borderRadius: 999, fontSize: 10, fontWeight: 800, background: '#fefce8', color: '#a16207', border: '1px solid #fde68a' }}>DORMANT</span>}
          </div>
          </div>
        </div>
      )
    },
    { header: 'Phone', accessor: 'phone' },
    {
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} map={CLIENT_STATUS_MAP} />,
    },
    {
      header: 'Branch',
      render: (row) => row.branch ? (typeof row.branch === 'object' ? row.branch.name : row.branch) : '—',
    },
    {
      header: 'Date',
      render: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
    {
      header: 'Actions',
      render: (row) => {
        return (
          <div className="flex gap-1">
            <Link to={`/clients/${row._id}`} className="btn btn-outline btn-sm">
              👁️ View
            </Link>
            {row.status === 'deactivated' && (
              <button 
                type="button" 
                onClick={() => handleDelete(row._id)}
                disabled={deletingId === row._id}
                title="Permanently Delete Client"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  background: deletingId === row._id ? '#f3f4f6' : '#fff',
                  border: '1px solid #fecaca',
                  color: deletingId === row._id ? '#9ca3af' : '#dc2626',
                  cursor: deletingId === row._id ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                {deletingId === row._id ? (
                  <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
                ) : (
                  <span style={{ fontSize: 16 }}>🗑️</span>
                )}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  // Get status options from CLIENT_STATUS_MAP
  const statusLabels = { onboarding: 'Onboarding', 'pending-approval': 'Pending Approval', 'pending-manager-review': 'Pending Manager Review', 'returned-for-modification': 'Returned for Modification', approved: 'Approved', rejected: 'Rejected', blacklisted: 'Blacklisted', inactive: 'Inactive', dormant: 'Dormant', deactivated: 'Deactivated' };
  const statusOptions = Object.keys(CLIENT_STATUS_MAP || {}).map((key) => ({ value: key, label: statusLabels[key] || key.replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()) }));

  return (
    <div style={{ color: '#111827' }}>
      {/* Top Bar: Search + Actions */}
      <div className="flex justify-between items-center mb-3">
        <div style={{ position: 'relative' }}>
          <input 
            className="form-control" 
            style={{ maxWidth: '300px', paddingLeft: 35 }} 
            placeholder="Search by name, phone, or National ID..." 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
          />
          {loading && (
            <span style={{ 
              position: 'absolute', 
              right: 10, 
              top: '50%', 
              transform: 'translateY(-50%)',
              animation: 'spin 1s linear infinite',
              display: 'inline-block',
              fontSize: '14px'
            }}>
              ⏳
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {/* Export Button with Dropdown */}
          <div style={{ position: 'relative' }} ref={exportMenuRef}>
            <button 
              className="btn btn-secondary" 
              onClick={() => !isExporting && setShowExportMenu(!showExportMenu)}
              disabled={isExporting}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {isExporting ? (
                <>
                  <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
                  Exporting...
                </>
              ) : (
                <>
                  📥 Export
                  <span style={{ fontSize: 10, opacity: 0.7 }}>▼</span>
                </>
              )}
            </button>
            
            {showExportMenu && !isExporting && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 4,
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                minWidth: 200,
                zIndex: 50,
                overflow: 'hidden',
                animation: 'slideDown 0.15s ease-out'
              }}>
                <div style={{ 
                  padding: '8px 14px', 
                  fontSize: 11, 
                  fontWeight: 600, 
                  color: '#6b7280', 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  Export Format
                </div>
                <button
                  onClick={() => handleExport('csv')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: '12px 14px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: 14,
                    color: '#374151',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ fontSize: 20 }}>📄</span>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 500 }}>CSV File</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>Comma-separated values</div>
                  </div>
                </button>
                <button
                  onClick={() => handleExport('excel')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: '12px 14px',
                    border: 'none',
                    borderTop: '1px solid #f3f4f6',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: 14,
                    color: '#374151',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ fontSize: 20 }}>📊</span>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 500 }}>Excel File</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>.xls spreadsheet format</div>
                  </div>
                </button>
              </div>
            )}
          </div>
          
          {/* Quick dormant filter */}
          <button
            className={`btn ${clientTypeFilter === 'dormant' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setClientTypeFilter(clientTypeFilter === 'dormant' ? '' : 'dormant')}
            title="Show dormant clients"
          >
            💤 Dormant
          </button>

          {canViewApprovals && (
            <Link to="/clients/approvals" className="btn btn-warning">
              Pending Approvals
            </Link>
          )}
        </div>
      </div>

      {/* Filter Panel */}
      {true && (
        <div className="card mb-3" style={{
          padding: '20px 24px',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid #dbe3ef',
          borderRadius: 14,
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
          animation: 'slideDown 0.2s ease-out'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: 16
          }}>
            <h6 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#374151' }}>
              🎛️ Filter Clients
            </h6>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                style={{
                  background: 'transparent',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  padding: '5px 14px',
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#6b7280',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fef2f2';
                  e.currentTarget.style.borderColor = '#fecaca';
                  e.currentTarget.style.color = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                ✕ Clear All
              </button>
            )}
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 18
          }}>
            {/* Client Category Filter */}
            <div>
              <label style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: '#6b7280',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}>
                Client Category
              </label>
              <select
                className="form-control"
                value={clientTypeFilter}
                onChange={(e) => setClientTypeFilter(e.target.value)}
                style={{ fontSize: 14 }}
              >
                <option value="">All Clients</option>
                <option value="new">🆕 New Clients</option>
                <option value="inactive">⏸ Inactive (No Loan)</option>
                <option value="dormant">💤 Dormant Clients</option>
                <option value="high-risk">⚠️ High Risk Clients</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: 12, 
                fontWeight: 600, 
                color: '#6b7280',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}>
                Status
              </label>
              <select
                className="form-control"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ fontSize: 14 }}
              >
                <option value="">All Statuses</option>
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            {/* Branch Filter */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: 12, 
                fontWeight: 600, 
                color: '#6b7280',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}>
                Branch
              </label>
              <select
                className="form-control"
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                style={{ fontSize: 14 }}
              >
                <option value="">All Branches</option>
                {branches.map(branch => (
                  <option key={branch._id} value={branch._id}>{branch.name}</option>
                ))}
              </select>
            </div>
            
            {/* Date From */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: 12, 
                fontWeight: 600, 
                color: '#6b7280',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}>
                Created From
              </label>
              <input
                type="date"
                className="form-control"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ fontSize: 14 }}
              />
            </div>
            
            {/* Date To */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: 12, 
                fontWeight: 600, 
                color: '#6b7280',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}>
                Created To
              </label>
              <input
                type="date"
                className="form-control"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ fontSize: 14 }}
              />
            </div>
          </div>
          
          {/* Active Filters Tags */}
          {hasActiveFilters && (
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 8, 
              marginTop: 16, 
              paddingTop: 16, 
              borderTop: '1px solid #e5e7eb' 
            }}>
              <span style={{ fontSize: 12, color: '#9ca3af', alignSelf: 'center' }}>Active filters:</span>
              
              {clientTypeFilter && (
                <FilterTag
                  label={`Category: ${{ new: 'New Clients', inactive: 'Inactive (No Loan)', dormant: 'Dormant Clients', 'high-risk': 'High Risk Clients' }[clientTypeFilter] || clientTypeFilter}`}
                  color="#7c3aed"
                  bgColor="#f5f3ff"
                  borderColor="#ddd6fe"
                  onRemove={() => setClientTypeFilter('')}
                />
              )}

              {statusFilter && (
                <FilterTag 
                  label={`Status: ${statusOptions.find(o => o.value === statusFilter)?.label || statusFilter}`}
                  color="#1d4ed8"
                  bgColor="#eff6ff"
                  borderColor="#bfdbfe"
                  onRemove={() => setStatusFilter('')}
                />
              )}
              
              {branchFilter && (
                <FilterTag 
                  label={`Branch: ${branches.find(b => b._id === branchFilter)?.name || branchFilter}`}
                  color="#15803d"
                  bgColor="#f0fdf4"
                  borderColor="#bbf7d0"
                  onRemove={() => setBranchFilter('')}
                />
              )}
              
              {dateFrom && (
                <FilterTag 
                  label={`From: ${new Date(dateFrom).toLocaleDateString()}`}
                  color="#a16207"
                  bgColor="#fefce8"
                  borderColor="#fde68a"
                  onRemove={() => setDateFrom('')}
                />
              )}
              
              {dateTo && (
                <FilterTag 
                  label={`To: ${new Date(dateTo).toLocaleDateString()}`}
                  color="#a16207"
                  bgColor="#fefce8"
                  borderColor="#fde68a"
                  onRemove={() => setDateTo('')}
                />
              )}
            </div>
          )}
        </div>
      )}

      <div className="card">
        <DataTable 
          columns={columns} 
          data={clients} 
          pagination={pagination} 
          onPageChange={setPage} 
          loading={loading}
        />
      </div>
      {pagination && Number(pagination.pages) > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '16px 0 8px', padding: '14px 16px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 4px 14px rgba(15,23,42,.04)' }}>
          <span style={{ color: '#6b7280', fontSize: 13 }}>Page <strong style={{ color: '#111827' }}>{page}</strong> of <strong style={{ color: '#111827' }}>{pagination.pages}</strong> · {pagination.total || 0} clients</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ padding: '8px 11px', border: '1px solid #d1d5db', borderRadius: 8, background: page <= 1 ? '#f9fafb' : '#fff', color: page <= 1 ? '#9ca3af' : '#374151', cursor: page <= 1 ? 'not-allowed' : 'pointer' }}>‹ Prev</button>
            {Array.from({ length: Number(pagination.pages) }, (_, i) => i + 1).filter(n => n === 1 || n === Number(pagination.pages) || Math.abs(n - page) <= 2).reduce((items, n, i, arr) => { if (i > 0 && n - arr[i - 1] > 1) items.push('ellipsis-' + n); items.push(n); return items; }, []).map((item) => typeof item === 'string' ? <span key={item} style={{ padding: '8px 4px', color: '#9ca3af' }}>…</span> : <button type="button" key={item} aria-current={item === page ? 'page' : undefined} disabled={loading} onClick={() => setPage(item)} style={{ minWidth: 36, padding: '8px 11px', border: '1px solid ' + (item === page ? '#4f46e5' : '#d1d5db'), borderRadius: 8, background: item === page ? '#4f46e5' : '#fff', color: item === page ? '#fff' : '#374151', fontWeight: item === page ? 700 : 500, cursor: 'pointer' }}>{item}</button>)}
            <button type="button" disabled={page >= Number(pagination.pages) || loading} onClick={() => setPage(p => Math.min(Number(pagination.pages), p + 1))} style={{ padding: '8px 11px', border: '1px solid #d1d5db', borderRadius: 8, background: page >= Number(pagination.pages) ? '#f9fafb' : '#fff', color: page >= Number(pagination.pages) ? '#9ca3af' : '#374151', cursor: page >= Number(pagination.pages) ? 'not-allowed' : 'pointer' }}>Next ›</button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideDown {
          from { 
            opacity: 0; 
            transform: translateY(-8px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
      `}</style>
    </div>
  );
}

// Reusable Filter Tag Component
function FilterTag({ label, color, bgColor, borderColor, onRemove }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 10px 4px 12px',
      background: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 500,
      color: color
    }}>
      {label}
      <button 
        onClick={onRemove} 
        style={{
          background: 'none',
          border: 'none',
          color: color,
          cursor: 'pointer',
          padding: 0,
          fontSize: 16,
          lineHeight: 1,
          opacity: 0.7,
          transition: 'opacity 0.15s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
      >
        ×
      </button>
    </span>
  );
}